import { useMemo, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, AppBar, Avatar, Box, Button, Divider, IconButton, ListItemIcon,
  Menu, MenuItem, Stack, Toolbar, Tooltip, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { authService } from '../services/authService';
import configurationApi from '../services/configurationApi';
import { getMenuItemsForRole, roleLabels } from '../config/menuItems';
import { getHomeRoute } from '../config/roles';
import { BROKER_PORTAL_ROLES, canPerformBrokerMutations, isPendingBrokerCompany } from '../config/brokerAccess';
import NewLoanModal from '../pages/loan/components/NewLoanModal';
import RateLockBadge from '../pages/loan/components/RateLockBadge';
import RateLockAdvisorModal from '../pages/loan/components/RateLockAdvisorModal';
import Sidebar, { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from './components/Sidebar';
import ComplianceFooter from '../components/shared/ComplianceFooter';
import ColorModeToggle from '../components/shared/ColorModeToggle';

/** Slot id for pages to portal their per-page header content into the AppBar. */
export const PAGE_HEADER_SLOT_ID = 'app-page-header-slot';

const SIDEBAR_COLLAPSED_KEY = 'pro-portal-sidebar-collapsed';

const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1');
  const [showNewLoanModal, setShowNewLoanModal] = useState(false);
  const [showRateLockModal, setShowRateLockModal] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const { data: featureFlags = {} } = configurationApi.endpoints.getFeatureFlags.useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const role = currentUser?.role;
  const pendingBroker = isPendingBrokerCompany(currentUser?.brokerCompanyStatus);
  const isBrokerSection = BROKER_PORTAL_ROLES.includes(role);
  const canUseBrokerHeaderActions = canPerformBrokerMutations(role) && !pendingBroker;

  const handleLogout = () => {
    setAnchorElUser(null);
    authService.logout();
    navigate('/login');
  };

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, prev ? '0' : '1');
      return !prev;
    });
  };

  const menuItems = useMemo(() => {
    return getMenuItemsForRole(role, featureFlags, { pendingBroker });
  }, [featureFlags, pendingBroker, role]);

  const brandLink = getHomeRoute(role);
  const drawerWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  /** Sidebar footer — broker quick actions live here; identity moved to the AppBar user menu. */
  const sidebarFooter = (
    <Stack spacing={1.25}>
      {isBrokerSection && canUseBrokerHeaderActions && (
        <>
          <Box sx={{ display: 'flex' }}>
            <RateLockBadge onClick={() => setShowRateLockModal(true)} />
          </Box>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowNewLoanModal(true)}
          >
            New Loan
          </Button>
        </>
      )}
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Avatar src={currentUser?.avatar} alt={currentUser?.name} sx={{ width: 32, height: 32, fontSize: '0.85rem' }}>
          {currentUser?.name?.[0]}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {currentUser?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {roleLabels[role] || role}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        menuItems={menuItems}
        brandLink={brandLink}
        footer={sidebarFooter}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapsed}
      />

      {/* Main content area — offset by drawer width on desktop */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <AppBar
          position="fixed"
          color="transparent"
          elevation={0}
          sx={{
            width: { lg: `calc(100% - ${drawerWidth}px)` },
            ml: { lg: `${drawerWidth}px` },
            transition: 'width .2s ease, margin-left .2s ease',
            backdropFilter: 'blur(8px)',
            backgroundColor: (theme) => `rgba(${theme.vars.palette.background.paperChannel} / 0.82)`,
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 60 }, gap: 1.5 }}>
            {/* Mobile hamburger */}
            <IconButton
              edge="start"
              aria-label="open sidebar"
              onClick={() => setSidebarOpen(true)}
              sx={{ display: { lg: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {/* PRO Portal brand — always visible to anchor the header. */}
            <Box
              component={RouterLink}
              to={brandLink}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                flexShrink: 0,
                pr: 1.5,
                borderRight: { sm: 1 },
                borderColor: { sm: 'divider' },
              }}
            >
              <Box component="img" src="/assets/pp-logo.png" alt="PRO Portal" sx={{ height: 28 }} />
            </Box>

            {/*
             * Page header slot — pages portal their context (page title, actions, etc.) into
             * this region so the AppBar tracks them on scroll. Empty when no page provides one.
             */}
            <Box id={PAGE_HEADER_SLOT_ID} sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }} />

            {/* Global controls */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
              <ColorModeToggle />
              <Tooltip title={currentUser?.name || 'Account'}>
                <IconButton size="small" onClick={(e) => setAnchorElUser(e.currentTarget)} aria-label="user menu">
                  <Avatar src={currentUser?.avatar} alt={currentUser?.name} sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
                    {currentUser?.name?.[0]}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Stack>
            <Menu
              anchorEl={anchorElUser}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorElUser)}
              onClose={() => setAnchorElUser(null)}
              disableScrollLock
            >
              <Box sx={{ px: 2, py: 1, minWidth: 190 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{currentUser?.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {roleLabels[role] || role}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                <Typography variant="body2">Logout</Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 60 } }} />

        {/* Broker pending-approval banner */}
        {isBrokerSection && pendingBroker && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mx: 3, mt: 2 }}>
            Your company is pending approval. Quick Pricer is available now, and full broker features unlock after approval.
          </Alert>
        )}

        {/* Page content — keyed by route so navigation gets a subtle fade-in */}
        <Box
          component="main"
          key={location.pathname}
          className="page-fade-in"
          sx={{ flex: 1, px: { xs: 2, sm: 3 }, py: 2.5, width: '100%', maxWidth: 1680, mx: 'auto' }}
        >
          <Outlet />
        </Box>

        <ComplianceFooter />
      </Box>

      {/* Broker-only modals */}
      {isBrokerSection && canUseBrokerHeaderActions && (
        <>
          <RateLockAdvisorModal open={showRateLockModal} onClose={() => setShowRateLockModal(false)} />
          <NewLoanModal
            show={showNewLoanModal}
            onClose={() => setShowNewLoanModal(false)}
            mismoImportAdvisorEnabled={featureFlags.mismoImportAdvisor === true}
            onLoanCreated={(loan) => {
              setShowNewLoanModal(false);
              navigate(`/broker/loans/${loan.id}`, {
                state: {
                  postImportAction: loan.action,
                  pricingFilters: loan.pricingFilters,
                },
              });
            }}
          />
        </>
      )}
    </Box>
  );
};

export default AppLayout;
